import React, { useState, useEffect } from "react";
import type { MultiDayEntry } from "../types";
import type { Habit } from "../../habits/types";
import Container from "common/Container";
import Header from "components/Header";
import Footer from "components/Footer";
import LargeText from "components/LargeText";
import LargePrompt from "components/LargePrompt";
import LargeCheck from "components/LargeCheck";
import { useSelector } from "react-redux";
import { getNextHabit } from "habits/selectors";
import { getHabitUrl } from "habits/util";
import { useNavigate } from "react-router-dom";
import type { State } from "store";
import { getBeginingOfDayInMs } from "common/time";

export type Props = {
  log: MultiDayEntry[] | null | undefined;
  now: number;
  time: number;
  habitId: string;
  habit: Habit;
  addLog: (arg0: MultiDayEntry) => void;
};

function getDayIndex({ log, habit }, beginningOfDay: number) {
  if (log == null || log.length === 0) {
    return 0;
  }

  let maxTime = 0;
  // we want the next index, so we need to add 1
  // if there is something off with the data, we want to default to 0
  let index = -1;
  log.forEach((entry: MultiDayEntry) => {
    if (beginningOfDay > entry.time && entry.time > maxTime) {
      maxTime = entry.time;
      index = entry.info.dayIndex;
    }
  });
  return (index + 1) % habit.extras.length;
}
/* this has a lot of similarities with Freeform, and might get merged at some point */

export default function MultiDay(props: Props) {
  const [content, setContent] = useState<{ [key: string]: string }>({});
  const [step, setStep] = useState(-1);
  useEffect(() => {
    setStep(-1);
  }, [props.habitId]);
  const nav = useNavigate();
  const nextHabit = useSelector<State, Habit | null>((state) =>
    getNextHabit(state, null)
  );

  const beginingOfDayInMs = getBeginingOfDayInMs(props.time);

  const days: Array<{
    text: string;
    prompts: string[];
  }> = props.habit.extras;
  const dayIndex = getDayIndex(props, beginingOfDayInMs);
  const day = days[dayIndex];

  const onClick = () => {
    if (step === day.prompts.length) {
      //redirect to the next habit?
      if (nextHabit != null && nextHabit.destination != null) {
        nav(getHabitUrl(nextHabit.destination, nextHabit.habitId));
      } else {
        nav("/");
      }

      return;
    }
    const newStep = step + 1;
    setStep(newStep);
    if (newStep === day.prompts.length) {
      const logEntry: MultiDayEntry = {
        type: props.habitId,
        time: props.time,
        info: {
          content,
          dayIndex: dayIndex,
        },
      };
      props.addLog(logEntry);
      // navigate to next
    }
  };
  const onPromptChange = (prompt: string, text: string) => {
    const newContent = { ...content, [prompt]: text };
    setContent(newContent);
  };

  const title = props.habit.title;

  let body = null;

  if (step === -1) {
    body = <LargeText>{props.habit.description}</LargeText>;
  } else if (step === day.prompts.length) {
    body = <LargeCheck />;
  } else {
    const prompt = day.prompts[step];
    body = (
      <LargePrompt
        key={prompt}
        prompt={prompt}
        onChangeText={(text) => onPromptChange(prompt, text)}
      />
    );
  }

  return (
    <Container>
      <Header title={title} />
      {body}
      <Footer onClick={onClick} enabled={true} />
    </Container>
  );
}
